import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  initialize,
  LDClient,
  LDFlagChangeset,
  LDFlagSet,
  LDFlagValue,
  LDSingleKindContext
} from 'launchdarkly-js-client-sdk';
import { EnvConfig } from './env-config';
import { UserHashRequest, UserHashResponse } from '../model/user-hash';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../user/services/user.service';

/** Feature Flag service */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  /** The base URL */
  private baseUrl = this.env.getUrl().apiURL;
  /** The environment name */
  private envName: string;

  /** The LaunchDarkly client */
  private client: LDClient;
  /** The client user ID */
  private userId: string = null;

  /** The feature flags behavior subject */
  private flags$: BehaviorSubject<LDFlagSet> = new BehaviorSubject<LDFlagSet>({});

  /** The map of flag to value */
  private flagMap: Map<string, Observable<LDFlagValue>> = new Map();

  /**
   * Whether LaunchDarkly has been initialized.
   * Note: This does NOT represent if the initialization was successful.
   */
  private isInitialized: boolean = false;

  /** The anonymous LaunchDarkly client */
  private readonly anonymousContext: LDSingleKindContext = {
    kind: 'user',
    key: 'anonymous',
    anonymous: true
  };

  constructor(
    private http: HttpClient,
    private env: EnvConfig,
    private userService: UserService
  ) {
    this.envName = env.getEnvName();
    if (!this.envName) {
      console.error('Unable to initialize LaunchDarkly client due to missing environment name');
      return;
    }

    // Set initial LaunchDarkly context
    const clientSdkKey = this.getClientSdkKey(this.envName);
    if (!clientSdkKey) {
      console.error(`No LaunchDarkly client SDK key found for environment: ${this.envName}`);
      return;
    }

    this.client = initialize(clientSdkKey, this.anonymousContext);

    this.client.on('ready', () => {
      this.isInitialized = true;
      const allFlags = this.client.allFlags();
      this.flags$.next(allFlags);
    });

    this.client.on('change', (changes: LDFlagChangeset) => {
      const updatedFlags = { ...this.flags$.value };
      Object.keys(changes).forEach(flagKey => {
        updatedFlags[flagKey] = changes[flagKey].current;
      });
      this.flags$.next(updatedFlags);
    });

    // Update launch darkly context if userId changes
    this.userService.userId.subscribe((userId) => {
      if (this.client && this.userId === userId) {
        return;
      }

      this.userId = userId;
      const hasUserId = userId != null && userId !== '' && userId !== 'undefined';
      if (hasUserId) {
        const context: LDSingleKindContext = {
          kind: 'user',
          key: `${userId}`,
          anonymous: false
        };

        // Generate user hash if client is logged in
        this.generateUserHash(userId).subscribe((res: UserHashResponse) => {
          if (res.error) {
            console.error('User hash generation for LaunchDarkly failed:', res.error);
          } else {
            this.client.identify(context, res.hash).then((allFlags: LDFlagSet) => {
              this.flags$.next(allFlags);
            }).catch((err) => {
              console.error('LaunchDarkly identify failed:', err);
            });
          }
        });
      } else {
        // Make anonymous client
        this.client.identify(this.anonymousContext).then((allFlags: LDFlagSet) => {
          this.flags$.next(allFlags);
        }).catch((err) => {
          console.error('LaunchDarkly identify failed:', err);
        });
      }
    });
  }

  /**
   * Gets the flag value for the specified feature flag key
   * @param flagKey The feature flag key
   * @param defaultValue The default flag value
   * @returns The flag value
   */
  public getFlagValue(flagKey: string, defaultValue: LDFlagValue = false): Observable<LDFlagValue> {
    if (!this.client) {
      // Return an observable with the default value if the client was never created
      return new BehaviorSubject<LDFlagValue>(defaultValue).asObservable();
    }

    // If an Observable for this flag already exists, return it
    if (this.flagMap.has(flagKey)) {
      return this.flagMap.get(flagKey);
    }

    // Set the current flag value
    const flagValue$ = new BehaviorSubject<LDFlagValue>(
      this.isInitialized ? this.client.variation(flagKey, defaultValue) : defaultValue
    );

    // Listen for changes to the specific flag
    this.client.on(`change:${flagKey}`, (newValue: LDFlagValue) => {
      flagValue$.next(newValue);
    });

    if (!this.isInitialized) {
      // If the client is not initialized, get the flag value once it's ready
      this.client.waitUntilReady().then(() => {
        flagValue$.next(this.client.variation(flagKey, defaultValue));
      });
    }

    this.flagMap.set(flagKey, flagValue$.asObservable());
    return flagValue$.asObservable();
  }

  /**
   * Gets all the LaunchDarkly feature flags
   * @returns The feature flag set
   */
  public getAllFlags(): Observable<LDFlagSet> {
    return this.flags$.asObservable();
  }

  /**
   * Gets the client SDK key based on the environment name
   * @param envName The environment name
   * @returns The client SDK key
   */
  private getClientSdkKey(envName: string): string {
    switch (envName) {
      case 'beta':
        return '68657833f45cae092340dd94';
      case 'green':
        return '68657833f45cae092340dd90';
      case 'local': // Default local env to orange
      case 'orange':
        return '68657833f45cae092340dd8a';
      case 'prod':
        return '68657833f45cae092340dd96';
      case 'purple':
        return '68657833f45cae092340dd92';
      case 'red':
        return '68657833f45cae092340dd8e';
      case 'yellow':
        return '68657833f45cae092340dd8c';
      default:
        return '';
    }
  }

  /**
   * Generates the user hash for LaunchDarkly
   * @param userId The user ID to generate a hash for
   * @returns The generated hash response
   */
  private generateUserHash(userId: string): Observable<UserHashResponse> {
    const url = `${this.baseUrl}/api/features/user-hash`;
    const payload: UserHashRequest = { userId };
    return this.http.post<UserHashResponse>(url, payload);
  }
}
